import { documentReady } from '@the-shared/document-ready';

type Callback = ( element: HTMLElement ) => void;

const liveElementCallbacks = new Map< string, Map< Symbol, Callback > >();
const processedElements = new Map< Symbol, WeakSet< HTMLElement > >();

const runCallback = (
	element: HTMLElement,
	callback: Callback,
	key: Symbol
) => {
	if ( ! processedElements.has( key ) ) {
		processedElements.set( key, new WeakSet() );
	}

	if ( ! processedElements.get( key ).has( element ) ) {
		requestAnimationFrame( () => callback( element ) );
		processedElements.get( key ).add( element );
	}
};

const observer = new MutationObserver( ( mutations ) => {
	mutations.forEach( ( mutation ) => {
		if ( mutation.type === 'childList' ) {
			mutation.addedNodes.forEach( ( node ) => {
				if ( node instanceof HTMLElement ) {
					liveElementCallbacks.forEach( ( callbacks, selector ) => {
						if ( node.matches( selector ) ) {
							callbacks.forEach( ( callback, key ) => {
								runCallback( node, callback, key );
							} );
						}

						node.querySelectorAll< HTMLElement >(
							selector
						).forEach( ( element ) => {
							callbacks.forEach( ( callback, key ) => {
								runCallback( element, callback, key );
							} );
						} );
					} );
				}
			} );
		}
	} );
} );

documentReady( () => {
	observer.observe( document.body, { childList: true, subtree: true } );
} );

function liveElement< T extends HTMLElement >(
	selector: string,
	callback: ( element: T ) => void
): () => void {
	if ( ! liveElementCallbacks.has( selector ) ) {
		liveElementCallbacks.set( selector, new Map() );
	}

	const key = Symbol();

	liveElementCallbacks.get( selector ).set( key, callback );

	documentReady( () => {
		document
			.querySelectorAll< HTMLElement >( selector )
			.forEach( ( element ) => {
				runCallback( element, callback, key );
			} );
	} );

	return () => {
		liveElementCallbacks.get( selector ).delete( key );
		if ( 0 === liveElementCallbacks.get( selector ).size ) {
			liveElementCallbacks.delete( selector );
		}
	};
}

export { liveElement };
